//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UnisonColor, UnisonIconType, theme } from '@bvengineering/unison-core';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { YOUTUBE_ICON_MIN_SIZE } from '@components/atoms/icon/iconConstants';
import Popover from '@components/molecules/popover/Popover';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

export type ActionButtonSize = 'extra-small' | 'small' | 'large';

interface IOwnProps {
  disabledPopoverContent?: React.ReactChild;
  iconColor?: UnisonColor;
  iconName: UnisonIconType;
  isDisabled?: boolean;
  label?: string;
  size?: ActionButtonSize;
  trackingId?: string;
}
type ButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;
type Props = IOwnProps &
  Partial<ITrackableComponent> &
  Pick<React.ComponentProps<typeof Popover>, 'interactive' | 'placement'> &
  Omit<ButtonProps, 'ref'>;

/**
 * The ActionButton atom renders a round button with an icon and a transparent background and
 * optional label that appear on hover, with optional sizing and icon color configurations.
 *
 * If the button is disabled and we have contextual text to show, a standard tooltip will appear
 * on hover with the given disabledPopoverContents instead of the black label.
 */
const ActionButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className = '',
      disabledPopoverContent,
      iconColor,
      iconName,
      interactive,
      isDisabled = false,
      label,
      placement = 'bottom',
      size = 'small',
      trackingId,
      type = 'button',
      ...buttonProps
    },
    ref,
  ) => {
    const button = (
      <span>
        <ActionButtonButton
          ref={ref}
          aria-label={label ?? iconName}
          disabled={isDisabled}
          size={size}
          trackingId={trackingId}
          type={type}
          {...buttonProps}
        >
          <ActionIcon
            $iconColor={iconColor}
            $isDisabled={isDisabled}
            $size={size}
            name={iconName}
          />
        </ActionButtonButton>
      </span>
    );

    /**
     * We only want to show the outer tooltip with contextual information if
     * we have text to show in it and the ActionButton itself is disabled.
     */
    const shouldShowRegularTooltip = isDisabled && disabledPopoverContent;

    /**
     * Only show the black label if the ActionButton itself is enabled
     * and we have a label to show. This means that an ActionButton that
     * is disabled and has no contextual information under the
     * disabledPopoverContent prop will have no attached popover.
     */
    const shouldShowLabel = !isDisabled && label;

    if (shouldShowRegularTooltip) {
      return (
        <div className={className}>
          <SmallTooltip
            appendTarget='body'
            content={disabledPopoverContent}
            interactive={interactive}
            target={button}
          />
        </div>
      );
    } else {
      return (
        <div className={className}>
          <PopoverWithContentOverrides
            appendTarget='body'
            content={<LabelWrapper>{label}</LabelWrapper>}
            interactive={interactive}
            isDisabled={!shouldShowLabel}
            offset={{ distance: 2 }}
            placement={placement}
            shouldOpenOnHover={true}
            shouldShowArrow={false}
            target={button}
          />
        </div>
      );
    }
  },
);

const actionButtonSizeMap = {
  ['extra-small']: {
    button: theme.spacings[4],
    icon: '12px',
  },
  small: {
    button: theme.spacings[5],
    icon: theme.spacings[3],
  },
  large: {
    button: theme.spacings[6],
    icon: theme.spacings[4],
  },
} as const;

const ActionButtonButton = withTracking(
  styled.button.attrs({
    className: 'cu-button-invisible',
  })<{ size: ActionButtonSize }>(
    ({ size }) => css`
      && {
        border-radius: 50%;
        width: ${actionButtonSizeMap[size].button};
        height: ${actionButtonSizeMap[size].button};
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: all !important;

        &:hover,
        &:focus-visible {
          background-color: rgba(0, 0, 0, 0.1);
        }
      }
    `,
  ),
);

const ActionIcon = styled(Icon)<{
  $size: ActionButtonSize;
  $iconColor?: UnisonColor;
  $isDisabled: boolean;
}>(
  ({ $iconColor, $isDisabled, $size, name, theme }) => css`
    opacity: ${$isDisabled ? 0.6 : 1};
    color: ${$iconColor ?? theme.colors.gray7};
    ${ActionButtonButton}:hover & {
      color: ${$iconColor ?? theme.colors.gray9};
    }
    width: ${name === 'youtube'
      ? `max(${YOUTUBE_ICON_MIN_SIZE}px, ${actionButtonSizeMap[$size].icon})`
      : actionButtonSizeMap[$size].icon};
    height: ${name === 'youtube'
      ? `max(${YOUTUBE_ICON_MIN_SIZE}px, ${actionButtonSizeMap[$size].icon})`
      : actionButtonSizeMap[$size].icon};
  `,
);

const LabelWrapper = styled.div(
  ({ theme }) => `
  max-width: ${theme.smallMeasure};
`,
);

// Applies overrides to the Popover content styles to achieve the desired label appearance
const PopoverWithContentOverrides = styled(Popover)(
  ({ theme }) => `
  && {
    &.tippy-box {
      background-color: rgba(0, 0, 0, 0.8);
      padding: ${theme.spacings[0]} ${theme.spacings[1]};
      color: ${theme.colors.white};
      font-size: ${theme.fontSizes[6]};
      filter: none;
      border: none;
    }
  }
`,
);

ActionButton.displayName = 'ActionButton';
export default ActionButton;
